import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { bugReportsAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import './Admin.css';

interface ApiUsage {
  date: string;
  usage: {
    osrm: { count: number; limit: number | null; percent: number | null };
    graphhopper: { count: number; limit: number; percent: number };
    ors: { count: number; limit: number; percent: number };
  };
  alertSent: boolean;
}

interface UsageHistoryDay {
  date: string;
  osrm: number;
  graphhopper: number;
  ors: number;
}

interface UsageHistory {
  days: number;
  limits: { graphhopper: number; ors: number };
  history: UsageHistoryDay[];
}

interface BugReportSummary {
  total: number;
  new: number;
  in_progress: number;
  resolved: number;
  closed: number;
}

interface RoutingStatus {
  graphhopper: { enabled: boolean; profiles: string[]; primary: boolean; note?: string };
  osrm: { enabled: boolean; profiles: string[]; note?: string };
  ors: { enabled: boolean; profiles: string[]; note?: string };
}

const Admin: React.FC = () => {
  const { t } = useLanguage();
  const [apiUsage, setApiUsage] = useState<ApiUsage | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistory | null>(null);
  const [routingStatus, setRoutingStatus] = useState<RoutingStatus | null>(null);
  const [bugStats, setBugStats] = useState<BugReportSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Infrastructure links
  const infrastructure = [
    { name: 'Vercel', url: 'https://vercel.com/dashboard', description: 'Frontend hosting', icon: '▲' },
    { name: 'Railway', url: 'https://railway.app/dashboard', description: 'Backend + PostgreSQL', icon: '🚂' },
    { name: 'Cloudflare', url: 'https://dash.cloudflare.com', description: 'DNS + SSL + Protection', icon: '☁️' },
    { name: 'Umami', url: 'https://analytics.umami.is', description: 'Analytics', icon: '📊' },
    { name: 'Firebase', url: 'https://console.firebase.google.com', description: 'Push Notifications', icon: '🔔' },
    { name: 'GraphHopper', url: 'https://graphhopper.com/dashboard', description: 'Routing API', icon: '🗺️' },
    { name: 'ORS', url: 'https://openrouteservice.org/dev/#/home', description: 'Routing Fallback', icon: '🛣️' },
    { name: 'Resend', url: 'https://resend.com/overview', description: 'Emails transactionnels', icon: '📧' },
    { name: 'Chrome DevTools', url: '#', description: 'Copier: chrome://inspect/#devices', icon: '🔧', copyUrl: 'chrome://inspect/#devices' },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch API usage stats
      const usageResponse = await fetch(`${API_URL}/routing/usage`);
      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        setApiUsage(usageData);
      }

      // Fetch usage history (last 14 days)
      const historyResponse = await fetch(`${API_URL}/routing/usage/history?days=14`);
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setUsageHistory(historyData);
      }

      // Fetch routing status
      const statusResponse = await fetch(`${API_URL}/routing/status`);
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setRoutingStatus(statusData);
      }

      // Fetch bug reports to compute stats
      try {
        const allReports = await bugReportsAPI.getAll();
        const stats: BugReportSummary = {
          total: allReports.length,
          new: allReports.filter((r: any) => r.status === 'new').length,
          in_progress: allReports.filter((r: any) => r.status === 'in_progress').length,
          resolved: allReports.filter((r: any) => r.status === 'resolved').length,
          closed: allReports.filter((r: any) => r.status === 'closed').length,
        };
        setBugStats(stats);
      } catch (bugErr) {
        console.warn('Could not fetch bug reports:', bugErr);
      }
    } catch (err: any) {
      setError('Erreur lors du chargement des données');
      console.error('Admin data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getUsageBarColor = (percent: number): string => {
    if (percent >= 80) return '#f44336';
    if (percent >= 50) return '#ff9800';
    return '#4caf50';
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Administration</h1>
        <button className="btn-refresh" onClick={fetchData}>
          Rafraichir
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-grid">
        {/* API Usage Section */}
        <section className="admin-section api-usage-section">
          <h2>API Routing - Usage du jour</h2>
          {apiUsage && (
            <>
              <p className="usage-date">Date: {apiUsage.date}</p>

              <div className="usage-cards">
                {/* OSRM */}
                <div className="usage-card">
                  <div className="usage-card-header">
                    <span className="provider-name">OSRM</span>
                    <span className="usage-badge unlimited">Illimité</span>
                  </div>
                  <div className="usage-count">{apiUsage.usage.osrm.count}</div>
                  <div className="usage-label">requêtes</div>
                  <div className="usage-note">Voiture uniquement</div>
                </div>

                {/* GraphHopper */}
                <div className="usage-card">
                  <div className="usage-card-header">
                    <span className="provider-name">GraphHopper</span>
                    <span
                      className="usage-badge"
                      style={{ backgroundColor: getUsageBarColor(apiUsage.usage.graphhopper.percent) }}
                    >
                      {apiUsage.usage.graphhopper.percent}%
                    </span>
                  </div>
                  <div className="usage-count">
                    {apiUsage.usage.graphhopper.count}
                    <span className="usage-limit">/ {apiUsage.usage.graphhopper.limit}</span>
                  </div>
                  <div className="usage-bar">
                    <div
                      className="usage-bar-fill"
                      style={{
                        width: `${apiUsage.usage.graphhopper.percent}%`,
                        backgroundColor: getUsageBarColor(apiUsage.usage.graphhopper.percent)
                      }}
                    />
                  </div>
                  <div className="usage-note">Vélo, Pied</div>
                </div>

                {/* ORS */}
                <div className="usage-card">
                  <div className="usage-card-header">
                    <span className="provider-name">ORS</span>
                    <span
                      className="usage-badge"
                      style={{ backgroundColor: getUsageBarColor(apiUsage.usage.ors.percent) }}
                    >
                      {apiUsage.usage.ors.percent}%
                    </span>
                  </div>
                  <div className="usage-count">
                    {apiUsage.usage.ors.count}
                    <span className="usage-limit">/ {apiUsage.usage.ors.limit}</span>
                  </div>
                  <div className="usage-bar">
                    <div
                      className="usage-bar-fill"
                      style={{
                        width: `${apiUsage.usage.ors.percent}%`,
                        backgroundColor: getUsageBarColor(apiUsage.usage.ors.percent)
                      }}
                    />
                  </div>
                  <div className="usage-note">Fallback (max 150km)</div>
                </div>
              </div>

              {apiUsage.alertSent && (
                <div className="alert-sent-notice">
                  Une alerte email a été envoyée aujourd'hui
                </div>
              )}
            </>
          )}
        </section>

        {/* Usage History Chart */}
        <section className="admin-section history-section">
          <h2>Historique des 14 derniers jours</h2>
          {usageHistory && usageHistory.history.length > 0 ? (
            <div className="history-chart">
              <div className="chart-legend">
                <span className="legend-item osrm"><span className="legend-color"></span>OSRM</span>
                <span className="legend-item graphhopper"><span className="legend-color"></span>GraphHopper</span>
                <span className="legend-item ors"><span className="legend-color"></span>ORS</span>
              </div>
              <div className="chart-container">
                {usageHistory.history.slice().reverse().map((day) => {
                  const maxValue = Math.max(
                    day.osrm,
                    day.graphhopper,
                    day.ors,
                    usageHistory.limits.graphhopper,
                    usageHistory.limits.ors
                  );
                  const scale = maxValue > 0 ? 100 / maxValue : 1;
                  const dateObj = new Date(day.date);
                  const dayLabel = dateObj.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });

                  return (
                    <div key={day.date} className="chart-day">
                      <div className="chart-bars">
                        <div
                          className="chart-bar osrm"
                          style={{ height: `${day.osrm * scale}%` }}
                          title={`OSRM: ${day.osrm}`}
                        ></div>
                        <div
                          className="chart-bar graphhopper"
                          style={{ height: `${day.graphhopper * scale}%` }}
                          title={`GraphHopper: ${day.graphhopper}/${usageHistory.limits.graphhopper}`}
                        ></div>
                        <div
                          className="chart-bar ors"
                          style={{ height: `${day.ors * scale}%` }}
                          title={`ORS: ${day.ors}/${usageHistory.limits.ors}`}
                        ></div>
                      </div>
                      <div className="chart-label">{dayLabel}</div>
                    </div>
                  );
                })}
              </div>
              <div className="chart-limits">
                <span>Limites: GraphHopper {usageHistory.limits.graphhopper}/jour, ORS {usageHistory.limits.ors}/jour</span>
              </div>
            </div>
          ) : (
            <div className="no-history-data">
              <p>Aucune donnée d'historique disponible.</p>
              <p className="hint">Les données apparaîtront après les premières requêtes de routing.</p>
            </div>
          )}
        </section>

        {/* Routing Services Status */}
        <section className="admin-section routing-status-section">
          <h2>Services de routing</h2>
          {routingStatus && (
            <div className="services-list">
              <div className={`service-item ${routingStatus.osrm.enabled ? 'enabled' : 'disabled'}`}>
                <span className="service-name">OSRM</span>
                <span className="service-status">{routingStatus.osrm.enabled ? 'Actif' : 'Inactif'}</span>
                <span className="service-profiles">{routingStatus.osrm.profiles.join(', ')}</span>
              </div>
              <div className={`service-item ${routingStatus.graphhopper.enabled ? 'enabled' : 'disabled'}`}>
                <span className="service-name">GraphHopper</span>
                <span className="service-status">
                  {routingStatus.graphhopper.enabled ? 'Actif' : 'Non configuré'}
                  {routingStatus.graphhopper.primary && ' (Principal)'}
                </span>
                <span className="service-profiles">
                  {routingStatus.graphhopper.profiles.join(', ') || 'Aucun profil'}
                </span>
              </div>
              <div className={`service-item ${routingStatus.ors.enabled ? 'enabled' : 'disabled'}`}>
                <span className="service-name">ORS</span>
                <span className="service-status">{routingStatus.ors.enabled ? 'Actif' : 'Non configuré'}</span>
                <span className="service-profiles">
                  {routingStatus.ors.profiles.join(', ') || 'Aucun profil'}
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Bug Reports Section */}
        <section className="admin-section bug-reports-section">
          <div className="section-header">
            <h2>Rapports de bugs</h2>
            <Link to="/bug-reports" className="btn-view-all">
              Voir tout
            </Link>
          </div>

          {bugStats && (
            <div className="bug-stats">
              <div className="bug-stat-card total">
                <span className="stat-value">{bugStats.total}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="bug-stat-card new">
                <span className="stat-value">{bugStats.new}</span>
                <span className="stat-label">Nouveaux</span>
              </div>
              <div className="bug-stat-card in-progress">
                <span className="stat-value">{bugStats.in_progress}</span>
                <span className="stat-label">En cours</span>
              </div>
              <div className="bug-stat-card resolved">
                <span className="stat-value">{bugStats.resolved}</span>
                <span className="stat-label">Résolus</span>
              </div>
              <div className="bug-stat-card closed">
                <span className="stat-value">{bugStats.closed}</span>
                <span className="stat-label">Fermés</span>
              </div>
            </div>
          )}

          {bugStats && bugStats.new > 0 && (
            <div className="bug-alert">
              {bugStats.new} nouveau{bugStats.new > 1 ? 'x' : ''} rapport{bugStats.new > 1 ? 's' : ''} à traiter
            </div>
          )}
        </section>

        {/* Quick Info Section */}
        <section className="admin-section info-section">
          <h2>Informations</h2>
          <div className="info-list">
            <div className="info-item">
              <span className="info-label">Environnement</span>
              <span className="info-value">{process.env.NODE_ENV}</span>
            </div>
            <div className="info-item">
              <span className="info-label">API URL</span>
              <span className="info-value">{API_URL}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Version</span>
              <span className="info-value">1.0.0</span>
            </div>
          </div>
        </section>

        {/* Infrastructure Section */}
        <section className="admin-section infrastructure-section">
          <h2>Infrastructure</h2>
          <div className="infrastructure-grid">
            {infrastructure.map((service) => (
              <a
                key={service.name}
                href={service.url}
                target={service.copyUrl ? undefined : "_blank"}
                rel={service.copyUrl ? undefined : "noopener noreferrer"}
                className="infrastructure-card"
                onClick={service.copyUrl ? (e) => {
                  e.preventDefault();
                  navigator.clipboard.writeText(service.copyUrl!);
                  alert('URL copiée ! Collez dans la barre d\'adresse Chrome:\n' + service.copyUrl);
                } : undefined}
              >
                <span className="infra-icon">{service.icon}</span>
                <div className="infra-info">
                  <span className="infra-name">{service.name}</span>
                  <span className="infra-description">{service.description}</span>
                </div>
                <span className="infra-arrow">{service.copyUrl ? '📋' : '→'}</span>
              </a>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Admin;
